import useUpdatePartialUserApi, {
  UpdatePartialUserInput,
} from '@/api/useUpdatePartialUserApi'
import useUpdateUserApi, {
  UpdateUserInput,
} from '@/app/book/api/useUpdateUserApi'
import { feedbackOptions } from '@/data/feedbackOptions'
import { otherHearAboutUsOptions } from '@/data/otherHearAboutUsOptions'
import SelectInput from 'lib/components/Form/SelectInput/SelectInput'
import { SubBrandName } from 'lib/settings/brands/index'
import { subBrands } from 'lib/settings/brands/sub-brands'
import { emailPattern } from 'lib/utilities/string'
import { useMemo, useState } from 'react'
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form'
import Button from '../Button/Button'
import CheckboxInput from '../Form/CheckboxInput/CheckboxInput'
import CountryInput from '../Form/CountryInput/CountryInput'
import PasswordInput from '../Form/PasswordInput/PasswordInput'
import PhoneInput from '../Form/PhoneInput/PhoneInput'
import TextInput from '../Form/TextInput/TextInput'
import Modal from '../Modal/Modal'
import { COMPLETE_REGISTRATION_MODAL_ID } from './constants'

export interface CompleteRegistrationModalProps {
  /**
   * The brand to use.
   */
  brand: SubBrandName
  /**
   * Initial values for the form fields
   */
  defaultValues?: Record<string, any>
  /**
   * If true then the form will show the overage terms checkbox
   */
  overageTerms?: boolean
  /**
   * Callback function when the form is submitted.
   */
  onSubmit: (data: Record<string, any>) => void
  /**
   * If true then the form will use the useUpdatePartialUserApi mutation
   */
  partialUser?: boolean
  /*
   * User id for the partial user
   */
  userId?: string | null
  /*
   * Order id for the partial user
   */
  orderId?: string | null
  /*
   * Tracking id for the partial user
   */
  trackingId?: string | null
}

export const MODAL_ID = COMPLETE_REGISTRATION_MODAL_ID

type FormInput = {
  firstName?: string
  lastName?: string
  email?: string
  country?: string
  phone?: string
  password?: string
  confirmPassword?: string
  smsTrackingUpdatesOptIn?: boolean
  howDidYouHearAboutUs?: string
  otherHearAboutUs?: string
  terms?: boolean
  oversize?: boolean
}

type UserInput = UpdateUserInput | UpdatePartialUserInput

/** Fields this form actually renders — a server error naming anything else
 *  cannot be shown inline, so it falls back to the form-level message. */
const FORM_FIELD_NAMES: Array<keyof FormInput> = [
  'firstName',
  'lastName',
  'email',
  'country',
  'phone',
  'password',
  'confirmPassword',
  'smsTrackingUpdatesOptIn',
  'howDidYouHearAboutUs',
  'otherHearAboutUs',
  'terms',
  'oversize',
]

export default function CompleteRegistrationModal({
  brand = 'shipplay',
  defaultValues = {},
  overageTerms = false,
  onSubmit,
  partialUser = false,
  userId,
  orderId,
  trackingId,
}: CompleteRegistrationModalProps) {
  const { handleSubmit, control, setError } = useForm()
  // Anything the server rejects that isn't attributable to a single field.
  // Without this the mutation could fail (GraphQL error, network failure, an
  // `errors` payload naming a field this form doesn't render) and the UI would
  // not change at all — the "Submit does nothing" report in GSE-12002.
  const [submitError, setSubmitError] = useState<string | null>(null)

  const updateUserMutation = useUpdateUserApi()
  const partialUserMutation = useUpdatePartialUserApi()

  const { mutate, isPending } = partialUser
    ? partialUserMutation
    : updateUserMutation

  const GENERIC_SUBMIT_ERROR =
    'We could not complete your registration. Please try again, or contact support if this keeps happening.'

  /**
   * Pulls a displayable sentence out of a thrown mutation error.
   *
   * NEVER render `error.message` directly: graphql-request's ClientError builds
   * it by serialising the whole response AND the request, so the raw string
   * contains the full query and its variables — including the password the
   * user just typed. Verified on staging: the alert rendered
   * `{"request":{"variables":{"input":{…,"password":"…"}}}}` on screen.
   *
   * Only the server-authored GraphQL error message is safe to show; anything
   * else falls back to the generic copy.
   */
  const displayableError = (error: unknown): string => {
    const gqlErrors = (
      error as { response?: { errors?: Array<{ message?: string }> } }
    )?.response?.errors

    const first = Array.isArray(gqlErrors)
      ? gqlErrors.find(
          (e) => typeof e?.message === 'string' && e.message.trim(),
        )
      : undefined

    const message = first?.message?.trim()

    // Guard against a server message that itself embeds a serialised payload.
    if (!message || message.length > 200 || /[{}]/.test(message)) {
      return GENERIC_SUBMIT_ERROR
    }

    return message
  }

  /**
   * Applies a server `errors` payload to the form, returning true when at
   * least one error landed on a rendered field. Tolerates every shape the
   * backend has produced: null, an array of `{field, message}`, an object map,
   * and entries whose `field` is missing or names something not in this form.
   */
  const applyFieldErrors = (errors: unknown): boolean => {
    if (!errors || typeof errors !== 'object') return false

    const entries = Object.values(errors as Record<string, unknown>)
    let applied = false

    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue

      const { field, message } = entry as { field?: string; message?: string }

      if (!field || !message) continue
      if (!FORM_FIELD_NAMES.includes(field as keyof FormInput)) continue

      setError(field as keyof FormInput, { type: 'custom', message })
      applied = true
    }

    return applied
  }

  const submitHandler: SubmitHandler<FormInput> = async (data) => {
    setSubmitError(null)

    const params: UserInput = {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      countryCode: data.country || '',
      phoneNumber: data.phone || '',
      terms: data.terms || false,
      smsTrackingOptinConfirmed: data.smsTrackingUpdatesOptIn || false,
      hearAboutUs: data.howDidYouHearAboutUs || '',
    }

    if (partialUser) {
      if (userId) {
        ;(params as UpdatePartialUserInput).userId = userId
      }

      if (orderId) {
        ;(params as UpdatePartialUserInput).orderId = orderId
      }

      if (trackingId) {
        ;(params as UpdatePartialUserInput).trackingId = trackingId
      }

      ;(params as UpdatePartialUserInput).password = data.password || ''
    }

    mutate(params, {
      onSuccess(data) {
        if (data?.success) {
          onSubmit?.(data)
          return
        }

        // Never leave the user staring at an unchanged form: if nothing could
        // be attached to a field, say something at the form level.
        if (!applyFieldErrors(data?.errors)) {
          setSubmitError(GENERIC_SUBMIT_ERROR)
        }
      },
      onError(error) {
        // graphql-request throws on a 200-with-`errors` body too, so this is
        // the path a rejected mutation actually takes.
        setSubmitError(displayableError(error))
      },
    })
  }

  const subBrand = subBrands[brand]

  // Every field here registers with react-hook-form through a Controller, and
  // a Controller captures `defaultValue` only when it first mounts. Modal
  // renders with `unmount={false}`, so these Controllers mount on page load —
  // before `getPartialUser` has answered. Defaults that land afterwards show
  // up in the inputs but never reach form state, so `required` fails on fields
  // that look filled and are disabled: submit then blocks with errors the user
  // cannot clear. Remounting on the defaults' identity re-registers every
  // field with the real values.
  const defaultsKey = `${defaultValues?.email ?? ''}|${defaultValues?.phoneNumber ?? ''}|${defaultValues?.firstName ?? ''}`

  const howDidYouHearAboutUs = useWatch({
    control,
    name: 'howDidYouHearAboutUs',
  })

  const showOtherHearAboutUs = useMemo(() => {
    if (!howDidYouHearAboutUs) {
      return false
    }
    return otherHearAboutUsOptions.includes(howDidYouHearAboutUs)
  }, [howDidYouHearAboutUs])

  return (
    <Modal
      id={MODAL_ID}
      persistent
      content={
        <form key={defaultsKey} onSubmit={handleSubmit(submitHandler)}>
          <h2 className="type-body-3 text-center">Complete registration</h2>
          <div className="grid grid-cols-1 gap-4 mt-6 lg:grid-cols-2">
            <TextInput
              control={control}
              name="firstName"
              label="First name*"
              placeholder="Enter first name"
              defaultValue={defaultValues?.firstName || ''}
              disabled={defaultValues?.firstName?.length}
              rules={{
                required: {
                  value: true,
                  message: 'Please provide your first name.',
                },
              }}
            />
            <TextInput
              control={control}
              name="lastName"
              label="Last name*"
              placeholder="Enter last name"
              defaultValue={defaultValues?.lastName || ''}
              disabled={defaultValues?.lastName?.length}
              rules={{
                required: {
                  value: true,
                  message: 'Please provide your last name.',
                },
              }}
            />
            <TextInput
              control={control}
              name="email"
              label="Email*"
              placeholder="Email address"
              defaultValue={defaultValues?.email || ''}
              disabled={defaultValues?.email?.length}
              rules={{
                required: { value: true, message: 'Email is required' },
                pattern: {
                  value: emailPattern,
                  message: 'Please provide a valid email',
                },
              }}
              className="lg:col-span-full"
            />
            <CountryInput
              control={control}
              name="country"
              label="Country of Residence*"
              defaultValue={defaultValues?.countryCode?.toLowerCase() || 'us'}
              disabled={defaultValues?.countryCode?.length}
              rules={{
                required: { value: true, message: 'Country is required' },
              }}
            />
            <PhoneInput
              control={control}
              name="phone"
              label="Phone number*"
              placeholder="555-555-5555"
              defaultValue={defaultValues?.phoneNumber || ''}
              disabled={defaultValues?.phoneNumber?.length}
              rules={{
                required: { value: true, message: 'Phone number is required' },
              }}
            />
            {partialUser ? (
              <>
                <PasswordInput
                  control={control}
                  name="password"
                  label="Password*"
                  rules={{
                    required: { value: true, message: 'Password is required' },
                  }}
                />
                <PasswordInput
                  control={control}
                  name="confirmPassword"
                  label="Confirm Password*"
                  rules={{
                    required: {
                      value: true,
                      message: 'Confirm password is required',
                    },
                    validate: (value, { password }) =>
                      value !== password ? 'Passwords do not match' : true,
                  }}
                />
              </>
            ) : null}

            <CheckboxInput
              label={`
                        Yes, I want to receive text message tracking updates regarding
                        my future shipments. I certify that this is my phone number.
                      `}
              name="smsTrackingUpdatesOptIn"
              control={control}
              className="[&]:items-start lg:col-span-full"
            />

            <SelectInput
              control={control}
              name="howDidYouHearAboutUs"
              label="How did you hear about us?"
              options={feedbackOptions}
              placeholder="Please select"
              className="md:col-span-full"
            />
            {showOtherHearAboutUs && (
              <TextInput
                control={control}
                name="otherHearAboutUs"
                className="[&]:items-start lg:col-span-full"
                label="Please Specify"
                rules={{}}
              />
            )}
          </div>
          <div className="mt-6">
            <CheckboxInput
              control={control}
              name="terms"
              label={
                <>
                  I agree to {subBrand.name}&apos;s{' '}
                  <a href="/terms" target="_blank">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank">
                    Privacy Policy.
                  </a>
                </>
              }
              className="[&]:items-start"
              rules={{
                required: {
                  value: true,
                  message: 'Please confirm that you agree to the terms.',
                },
              }}
            />
            {overageTerms ? (
              <CheckboxInput
                control={control}
                name="oversize"
                label={`I certify that each item does not exceed 60 inches in length, 130 inches in length plus girth, or 70 lbs. If an item exceeds these limits, I may incur additional charges up to $1,060. I agree that ${subBrand.name} may charge my provided payment method for any additional charges. Click here for details on metric calculations and surcharge amounts.`}
                className="[&]:items-start mt-3"
                rules={{
                  required: {
                    value: true,
                    message: 'Please confirm that you agree to the terms.',
                  },
                }}
              />
            ) : null}
          </div>
          {submitError && (
            <p
              role="alert"
              className="mt-6 text-center text-sm font-semibold text-red-600"
            >
              {submitError}
            </p>
          )}
          <div className="flex justify-center mt-6">
            <Button type="submit" disabled={isPending}>
              Submit
            </Button>
          </div>
        </form>
      }
    />
  )
}
